/**
 * Implementação do Repositório de FunnelStages usando Supabase
 * Camada de Infraestrutura
 */

import { supabase } from '../../lib/supabase';
import { IFunnelStageRepository } from '../../domain/repositories/IFunnelStageRepository';
import { FunnelStage } from '../../domain/entities/FunnelStage';
import { InfrastructureError } from '../../core/errors/DomainErrors';

export class SupabaseFunnelStageRepository implements IFunnelStageRepository {
    async findAll(funnelId?: string): Promise<FunnelStage[]> {
        let query = supabase
            .from('funnel_stages')
            .select('*')
            .order('order', { ascending: true });

        if (funnelId) {
            query = query.eq('funnel_id', funnelId);
        }

        const { data, error } = await query;

        if (error) {
            console.error('[REPOSITORY] Error fetching funnel stages:', error);
            throw new InfrastructureError(`Erro ao buscar estágios: ${error.message}`, error);
        }

        return data.map(this.mapToEntity);
    }

    async findById(id: string): Promise<FunnelStage | null> {
        const { data, error } = await supabase
            .from('funnel_stages')
            .select('*')
            .eq('id', id)
            .single();

        if (error) {
            if (error.code === 'PGRST116') {
                return null; // Not found
            }
            console.error('[REPOSITORY] Error fetching funnel stage:', error);
            throw new InfrastructureError(`Erro ao buscar estágio: ${error.message}`, error);
        }

        return this.mapToEntity(data);
    }

    async save(stage: FunnelStage, organizationId?: string): Promise<void> {
        const dbStage = this.mapToDatabase(stage);

        // Verifica se é novo (ID temporário, vazio ou undefined)
        const isNew = !stage.id || stage.id.startsWith('temp_');

        if (isNew) {
            // Remove ID temporário
            const { id, ...stageWithoutId } = dbStage;
            const insertPayload = {
                ...stageWithoutId,
                ...(organizationId ? { organization_id: organizationId } : {})
            };

            console.log('[REPOSITORY] Creating new funnel stage:', insertPayload);

            const { data, error } = await supabase
                .from('funnel_stages')
                .insert([insertPayload])
                .select();

            console.log('[REPOSITORY] Insert result:', { data, error });

            if (error) {
                console.error('[REPOSITORY] Error creating funnel stage:', error);
                console.error('[REPOSITORY] Error details:', {
                    message: error.message,
                    details: error.details,
                    hint: error.hint,
                    code: error.code
                });

                // Log completo do erro como string
                console.error('[REPOSITORY] Full error:', JSON.stringify(error, null, 2));

                throw new InfrastructureError(
                    `Erro ao criar estágio: ${error.message || 'Erro desconhecido'}. Código: ${error.code || 'N/A'}`,
                    error
                );
            }
        } else {
            const { error } = await supabase
                .from('funnel_stages')
                .update(dbStage)
                .eq('id', stage.id);

            if (error) {
                console.error('[REPOSITORY] Error updating funnel stage:', error);
                throw new InfrastructureError(`Erro ao atualizar estágio: ${error.message}`, error);
            }
        }
    }

    async delete(id: string): Promise<void> {
        console.log('[REPOSITORY] Deletando estágio do funil:', id);

        const { data, error } = await supabase
            .from('funnel_stages')
            .delete()
            .eq('id', id)
            .select();

        console.log('[REPOSITORY] Resultado da exclusão:', { data, error });

        if (error) {
            console.error('[REPOSITORY] Error deleting funnel stage:', error);
            throw new InfrastructureError(`Erro ao deletar estágio: ${error.message}`, error);
        }

        if (!data || data.length === 0) {
            throw new InfrastructureError('Estágio não encontrado ou já foi deletado');
        }

        console.log('[REPOSITORY] Estágio deletado com sucesso');
    }

    async count(): Promise<number> {
        const { count, error } = await supabase
            .from('funnel_stages')
            .select('*', { count: 'exact', head: true });

        if (error) {
            console.error('[REPOSITORY] Error counting funnel stages:', error);
            return 0;
        }

        return count || 0;
    }

    /**
     * Mapeia dados do banco para entidade de domínio
     */
    private mapToEntity(data: any): FunnelStage {
        return new FunnelStage(
            data.id,
            data.name,
            data.conversion_event || '',
            new Date(data.created_at),
            data.funnel_id,
            data.order || 0,
            data.deadline,
            data.color || null
        );
    }

    /**
     * Mapeia entidade de domínio para formato do banco
     */
    private mapToDatabase(stage: FunnelStage): any {
        return {
            id: stage.id,
            name: stage.name,
            conversion_event: stage.conversionEvent,
            order: stage.order,
            color: stage.color || null,
            funnel_id: stage.funnelId,
            deadline: stage.deadline
        };
    }
}
